import { useCallback } from 'react'
import md5 from 'md5'
import { CreateDirectUploadMutation } from '@src/graphql/createDirectUpload.generated'
import { CreateFileFromSignedIdMutation } from '@src/graphql/createFileFromSignedId.generated'
import { FileMetadata, UserFileTag } from '@src/types'
import { useDispatchAsync } from '@src/utils'
import hexToBase64 from '@src/utils/hexToBase64'
import { Bugsnag } from '@src/config'
import { normalizeUploadErrorMessage } from '@src/utils/uploadErrors'

interface UploadFileParams {
  // Any URL the browser can fetch — in practice blob:, data:, or http(s):.
  uri: string
  fileTag: UserFileTag
  filename?: string
  contentType?: string
}

const defaultContentType = 'image/jpeg'
const defaultFilename = 'image.jpg'

export const useDirectUpload = () => {
  const dispatchAsync = useDispatchAsync()

  const uploadFile = useCallback(
    async ({
      uri,
      fileTag,
      contentType = defaultContentType,
      filename = defaultFilename,
    }: UploadFileParams) => {
      try {
        // fetch() doesn't reject on non-2xx, so an http(s) error page would
        // otherwise be hashed and uploaded as if it were the user's file.
        const sourceResponse = await fetch(uri)
        if (!sourceResponse.ok) {
          throw new Error(`Failed to read source uri (status ${sourceResponse.status})`)
        }
        const blob = await sourceResponse.blob()
        const buffer = await blob.arrayBuffer()
        const photoHash = md5(new Uint8Array(buffer))

        const upload = await dispatchAsync<CreateDirectUploadMutation['createDirectUpload']>({
          type: 'app/createDirectUpload',
          payload: {
            file: {
              checksum: hexToBase64(photoHash),
              filename,
              contentType,
              byteSize: blob.size,
            } as FileMetadata,
          },
        })

        const uploadResponse = await fetch(upload.url, {
          method: 'PUT',
          headers: upload.headers.reduce(
            (acc, { key, value }) => ({ ...acc, [key]: value }),
            {} as Record<string, string>,
          ),
          body: blob,
        })
        if (!uploadResponse.ok) {
          throw new Error(`Direct upload to S3 failed with status ${uploadResponse.status}`)
        }

        const file = await dispatchAsync<CreateFileFromSignedIdMutation['createFileFromSignedId']>({
          type: 'app/createFileFromSignedId',
          payload: {
            signedId: upload.signedId,
            fileTag,
          },
        })

        return file
      } catch (error: any) {
        // Group by the failure, not by the member's file path — see normalizeUploadErrorMessage.
        Bugsnag.notify(error, (event) => {
          // Bugsnag's onError contract is to mutate the event in place.
          // eslint-disable-next-line no-param-reassign
          event.groupingHash = normalizeUploadErrorMessage(error?.message)
          event.addMetadata('directUpload', {
            fileTag,
            filename,
            contentType,
            message: error?.message,
          })
        })
        return null
      }
    },
    [dispatchAsync],
  )

  return uploadFile
}
